const SLIDER_ROOT =
  "[data-slider], [data-input-type='slider'], [data-capital-slider]";

export const formatSliderValue = (amount, format) => {
  if (format === "euro") {
    const formatted = Number(amount)
      .toLocaleString("fr-FR")
      .replace(/\s/g, "\u202f");
    return `${formatted}\u00a0€`;
  }
  return String(amount);
};

export const snapSliderValue = (raw, min, max, step) => {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return min;
  const clamped = Math.min(max, Math.max(min, parsed));
  if (!step) return clamped;
  const snapped = min + Math.round((clamped - min) / step) * step;
  return Math.min(max, Math.max(min, snapped));
};

export const sliderRatio = (value, min, max) => {
  if (max <= min) return 0;
  return (value - min) / (max - min);
};

export const findSliderRoot = (el) => {
  if (!el) return null;
  if (el.closest) return el.closest(SLIDER_ROOT);
  return null;
};

const readRangeInput = (root) => {
  if (!root) return null;
  if (root.matches && root.matches("input[type='range']")) return root;
  return root.querySelector("input[type='range']");
};

const readBounds = (root, input) => {
  const min = Number(
    root.getAttribute("data-min") || (input && input.min) || 0,
  );
  const max = Number(
    root.getAttribute("data-max") || (input && input.max) || min,
  );
  const step = Number(
    root.getAttribute("data-step") || (input && input.step) || 1,
  );
  return { min, max, step };
};

export const syncSlider = (root) => {
  if (!root) return 0;
  const input = readRangeInput(root);
  if (!input) return 0;
  const { min, max, step } = readBounds(root, input);
  const value = snapSliderValue(input.value, min, max, step);
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);

  const format =
    root.getAttribute("data-slider-format") ||
    (root.hasAttribute("data-capital-slider") ? "euro" : undefined);
  const display = root.querySelector("[data-slider-display]");
  if (display) {
    display.textContent = formatSliderValue(value, format);
  }
  root.style.setProperty("--ratio", String(sliderRatio(value, min, max)));
  return value;
};

const stepSlider = (root, delta) => {
  const input = readRangeInput(root);
  if (!input) return;
  const { min, max, step } = readBounds(root, input);
  const next = snapSliderValue(
    Number(input.value) + Number(delta),
    min,
    max,
    step,
  );
  input.value = String(next);
  input.dispatchEvent(new Event("input", { bubbles: true }));
};

export const setupSliders = (doc, { onValueChange } = {}) => {
  if (!doc) return undefined;

  const sliders = doc.querySelectorAll(SLIDER_ROOT);
  for (const root of sliders) {
    syncSlider(root);
  }

  const onInput = (event) => {
    const input = event.target;
    if (!input || input.type !== "range") return;
    const root = findSliderRoot(input);
    if (!root) return;
    const value = syncSlider(root);
    if (onValueChange) onValueChange(String(value), input);
  };

  const onClick = (event) => {
    const btn = event.target && event.target.closest("[data-slider-step]");
    if (!btn) return;
    const root = findSliderRoot(btn);
    if (!root) return;
    event.preventDefault();
    stepSlider(root, btn.getAttribute("data-slider-step"));
  };

  const onKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const btn = event.target && event.target.closest("[data-slider-step]");
    if (!btn) return;
    const root = findSliderRoot(btn);
    if (!root) return;
    event.preventDefault();
    stepSlider(root, btn.getAttribute("data-slider-step"));
  };

  doc.addEventListener("input", onInput);
  doc.addEventListener("click", onClick);
  doc.addEventListener("keydown", onKeyDown);

  return () => {
    doc.removeEventListener("input", onInput);
    doc.removeEventListener("click", onClick);
    doc.removeEventListener("keydown", onKeyDown);
  };
};
